import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonProps,
  FormField,
  Multiselect,
  MultiselectProps,
  RadioGroup,
} from "@amzn/awsui-components-react";
import { OmsReport } from "../../lusca-search-api/generated-src";
import OMSColumnOrder from "./OMSColumnOrder";

export type OmsColumnsDraft = {
  strategy: "all" | "selected";
  names: string[];
  touched?: boolean;
  customOrder?: boolean;
};

export function columnSelectionError(
  settings: OmsColumnsDraft,
  report: OmsReport
): string | undefined {
  if (settings.strategy === "all") return undefined;
  if (!settings.names.length) return "Choose at least one column.";
  const missing = settings.names.filter(
    (name) => !report.columns.includes(name)
  );
  if (missing.length)
    return `Unavailable in this source: ${missing.slice(0, 3).join(", ")}${
      missing.length > 3 ? ` and ${missing.length - 3} more` : ""
    }. Remove or replace these columns.`;
  if (new Set(settings.names).size !== settings.names.length)
    return "Remove duplicate output columns.";
  return undefined;
}

const typeLabels = {
  string: "Text",
  number: "Number",
  date: "Date",
  datetime: "Date and time",
};

export default function OMSColumns({
  report,
  settings,
  onChange,
  focusRequested = false,
  onFocusHandled,
  attempted = false,
}: {
  report: OmsReport;
  settings: OmsColumnsDraft;
  onChange: (value: OmsColumnsDraft) => void;
  focusRequested?: boolean;
  onFocusHandled?: () => void;
  attempted?: boolean;
}) {
  const picker = useRef<MultiselectProps.Ref>(null);
  const pickerContainer = useRef<HTMLDivElement>(null);
  const orderAction = useRef<ButtonProps.Ref>(null);
  const wasOrdering = useRef(false);
  const [openPicker, setOpenPicker] = useState(false);
  const [ordering, setOrdering] = useState(false);
  useEffect(() => {
    if (wasOrdering.current && !ordering)
      (orderAction.current || picker.current)?.focus();
    wasOrdering.current = ordering;
  }, [ordering]);
  const error = columnSelectionError(settings, report);
  const visibleError =
    error && (attempted || settings.touched || settings.names.length > 0)
      ? error
      : undefined;
  useEffect(() => {
    if (!focusRequested && !openPicker) return undefined;
    const timer = window.setTimeout(() => {
      picker.current?.focus();
      const trigger = pickerContainer.current?.querySelector<HTMLButtonElement>(
        'button[aria-haspopup="dialog"], button[aria-haspopup="listbox"]'
      );
      if (openPicker && trigger?.getAttribute("aria-expanded") !== "true")
        trigger?.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      setOpenPicker(false);
      onFocusHandled?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [focusRequested, openPicker, onFocusHandled]);

  const changeNames = useCallback(
    (names: string[]) => {
      const ordered = settings.customOrder
        ? [
            ...settings.names.filter((name) => names.includes(name)),
            ...names.filter((name) => !settings.names.includes(name)),
          ]
        : [
            ...report.columns.filter((name) => names.includes(name)),
            ...names.filter((name) => !report.columns.includes(name)),
          ];
      onChange({ ...settings, names: ordered, touched: true });
    },
    [onChange, settings, report.columns]
  );
  const focusPicker = useCallback(() => {
    picker.current?.focus();
  }, []);
  return (
    <div className="oms-columns__layout">
      {!ordering && (
        <div className="oms-columns__selection">
          <FormField
            label="Columns to export"
            constraintText="All columns includes newly added fields."
          >
            <RadioGroup
              direction="horizontal"
              value={settings.strategy}
              items={[
                { value: "all", label: "All columns" },
                { value: "selected", label: "Selected columns" },
              ]}
              onChange={({ detail }) => {
                const strategy = detail.value as OmsColumnsDraft["strategy"];
                onChange({ ...settings, strategy });
                if (strategy === "selected") setOpenPicker(true);
              }}
            />
          </FormField>
          {settings.strategy === "all" && (
            <Box color="text-body-secondary">
              All {report.columns.length} columns in the report&apos;s original
              order.
            </Box>
          )}
          {settings.strategy === "selected" && (
            <div className="oms-columns__picker-controls">
              <FormField
                label="Column selection"
                errorText={visibleError}
                constraintText={
                  !visibleError && !settings.names.length
                    ? "Choose at least one column."
                    : undefined
                }
              >
                <div ref={pickerContainer}>
                  <Multiselect
                    expandToViewport
                    ref={picker}
                    ariaLabel="Output columns"
                    filteringType="auto"
                    filteringPlaceholder="Search available columns"
                    filteringAriaLabel="Search available columns"
                    filteringClearAriaLabel="Clear available column search"
                    placeholder={
                      settings.names.length
                        ? "Add or remove columns"
                        : "Choose columns"
                    }
                    empty="This report has no available columns."
                    noMatch="No matching columns."
                    keepOpen
                    enableSelectAll
                    tokenLimit={3}
                    i18nStrings={{
                      selectAllText: "Select all matching columns",
                      tokenLimitShowMore: "Show more selected columns",
                      tokenLimitShowFewer: "Show fewer selected columns",
                    }}
                    selectedOptions={settings.names.map((name) => ({
                      label: name,
                      value: name,
                    }))}
                    options={report.columns.map((name) => ({
                      label: name,
                      value: name,
                      description:
                        typeLabels[report.columnTypes?.[name] || "string"],
                    }))}
                    deselectAriaLabel={(option) =>
                      `Remove column ${option.value || "column"}`
                    }
                    onChange={({ detail }) =>
                      changeNames(
                        detail.selectedOptions.map(
                          (option) => option.value as string
                        )
                      )
                    }
                  />
                </div>
              </FormField>
            </div>
          )}
        </div>
      )}
      {settings.strategy === "selected" && settings.names.length > 0 && (
        <>
          <div className="oms-columns__ordering-action">
            <Box color="text-body-secondary">
              {settings.names.length} selected ·{" "}
              {settings.customOrder ? "Custom CSV order" : "Report order"}
            </Box>
            <Button ref={orderAction} onClick={() => setOrdering(!ordering)}>
              {ordering ? "Done reordering" : "Reorder columns"}
            </Button>
          </div>
          {ordering && (
            <OMSColumnOrder
              names={settings.names}
              reportOrder={report.columns}
              onChange={(names) =>
                onChange({
                  ...settings,
                  names,
                  customOrder: true,
                  touched: true,
                })
              }
              onEmpty={() => {
                setOrdering(false);
                setOpenPicker(true);
                focusPicker();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
